type Primitive = string | number | boolean | null | undefined;

/**
 * Creates a mutable model from a reactive initial value with change detection.
 *
 * @param initialValueRef - A ref containing the initial/original value
 * @returns An object with:
 *   - `model`: A mutable ref that can be edited
 *   - `hasChanges`: A computed boolean indicating if the model differs from the initial value
 *   - `isFilled`: A computed boolean indicating if the model has a meaningful value (not empty/null/undefined)
 *   - `reset`: A function to reset the model back to the initial value
 *
 * @example
 * ```ts
 * const initialName = ref('John');
 * const { model, hasChanges, isFilled, reset } = usePrimitiveModelWithChanges(initialName);
 *
 * model.value = 'Jane'; // hasChanges.value becomes true, isFilled.value is true
 * model.value = ''; // hasChanges.value becomes true, isFilled.value is false
 * reset(); // model.value becomes 'John' again, hasChanges.value becomes false
 * ```
 */
export function usePrimitiveModelWithChanges<T extends Primitive>(initialValueRef: Ref<T>) {
  // Create a mutable model initialized with the current initial value
  const model = ref<T>(initialValueRef.value);

  // Track whether the model has been modified from the initial value
  const hasChanges = computed(() => {
    return initialValueRef.value !== model.value;
  });

  // Track whether the model has a meaningful value (not empty/null/undefined)
  const isFilled = computed(() => {
    const value = model.value;

    switch (typeof value) {
      case "string":
        return value.replace(/<[^>]+>/g, "").trim().length > 0;
      case "number":
        return !isNaN(value);
      case "boolean":
        return true;
      case "undefined":
        return false;
      default:
        return false;
    }
  });

  // Sync model with initial value changes, but only if no pending changes exist
  // This prevents overwriting user modifications when the initial value updates
  watch(initialValueRef, (newValue, oldValue) => {
    if (model.value === oldValue) {
      // Model is still in sync with the old initial value, safe to update
      model.value = newValue;
    }
    // If model.value !== oldValue, user has made changes, so preserve them
  });

  // Reset model back to the current initial value
  function reset() {
    model.value = initialValueRef.value;
  }

  return {
    model,
    hasChanges,
    isFilled,
    reset,
  };
}
